import * as React from "react";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  MenuItem
} from "@mui/material";
import {
  Add,
  ArrowBack,
  Check,
  ChevronRight,
  Close,
  DeleteOutline,
  DragIndicator,
  Edit,
  ExpandMore,
  LibraryAdd
} from "@mui/icons-material";
import { translate } from "../../../../../../translate/translate";
import {
  calendarTypeLabels,
  Calendar,
  Template,
  TemplateTask,
  TemplateGroup
} from "../../../../../../models/basic/template";
import { NpiTemplateService } from "../../../../../../services/npi-template-service";
import { NpiTemplateGroupService } from "../../../../../../services/npi-template-group-service";
import { NpiTemplateTaskService } from "../../../../../../services/npi-template-task-service";
import { ErrorComponent } from "../../../../../../components/error/error.component";
import { URLS } from "../../../../../../services/routes";
import { NpiCalendarService } from "../../../../../../services/npi-calendar-service";
import { toast } from "react-toastify";
import "./npi-template-detail.css";
import CrudSelectProjectTypeComponent from "./crud-select-project-type.component";
import PredecessorSelectComponent, { getPredecessorIds, MENU_MIN_HEIGHT } from "./predecessor-select.component";
import { getReorderAnchor } from "./reorder-utils";
import { capitalizeFirstLetter } from "../../../../../../utils/format-string-utils";

type DraftTask = {
  name: string;
  acronym: string;
  status: string;
  resource: string;
  duration: number;
};

const emptyDraftTask: DraftTask = {
  name: "",
  acronym: "",
  status: "",
  resource: "",
  duration: 1
};

const ROOT_DROPPABLE_ID = "groups-root";
const MAX_SUBGROUP_DEPTH = 1;
const TASKS_TABLE_MIN_WIDTH = 1000;

const NpiTemplateDetailComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const service = useMemo(() => new NpiTemplateService(), []);
  const groupService = useMemo(() => new NpiTemplateGroupService(), []);
  const taskService = useMemo(() => new NpiTemplateTaskService(), []);
  const calendarService = useMemo(() => new NpiCalendarService(), []);

  const [template, setTemplate] = React.useState<Template>(new Template());
  const [groups, setGroups] = React.useState<TemplateGroup[]>([]);
  const [calendarList, setCalendarList] = React.useState<Calendar[]>([]);
  const [calendarOptTasks, setCalendarOptTasks] = React.useState<Calendar[]>([]);

  const [addingGroupParentId, setAddingGroupParentId] = React.useState<number | null | undefined>(undefined);
  const [addingGroupName, setAddingGroupName] = React.useState<string>("");
  const [loadingRequest, setLoadingRequest] = React.useState<boolean>(false);

  const [editingGroupId, setEditingGroupId] = React.useState<number | null>(null);
  const [editingGroupName, setEditingGroupName] = React.useState<string>("");

  const [addingTaskGroupId, setAddingTaskGroupId] = React.useState<number | null>(null);
  const [draftTask, setDraftTask] = React.useState<DraftTask>(emptyDraftTask);

  const [taskIds, setTaskIds] = React.useState<Set<number>>(new Set());
  const [deleteGroupId, setDeleteGroupId] = React.useState<number | null>(null);
  const [deleteTask, setDeleteTask] = React.useState<{ groupId: number; taskId: number } | null>(null);
  const [editingNameTaskId, setEditingNameTaskId] = React.useState<number | null>(null);
  const [collapsedGroupIds, setCollapsedGroupIds] = React.useState<Set<number>>(new Set());

  const markTaskTouched = (taskId: number) => {
    setTaskIds((current) => (current.has(taskId) ? current : new Set(current).add(taskId)));
  };

  const expandGroup = (groupId: number) => {
    setCollapsedGroupIds((current) => {
      if (!current.has(groupId)) {
        return current;
      }

      const next = new Set(current);
      next.delete(groupId);
      return next;
    });
  };

  const handleToggleGroupCollapse = (groupId: number) => {
    setCollapsedGroupIds((current) => {
      const next = new Set(current);

      if (next.has(groupId)) {
        next.delete(groupId);
        return next;
      }

      next.add(groupId);
      return next;
    });
  };

  const initialTemplateRef = React.useRef<string>("");
  const groupsEndRef = React.useRef<HTMLDivElement | null>(null);
  const shouldScrollToNewGroupRef = React.useRef(false);

  const getTemplateSnapshot = (currentTemplate: Template) => JSON.stringify({
    name: currentTemplate.name.trim(),
    project_type: currentTemplate.project_type,
    calendars: [...currentTemplate.calendars].sort((a, b) => a - b),
    active: currentTemplate.active,
  });

  useEffect(() => {
    if (!id) {
      return;
    }

    service.readById(id).then((response) => {
      setTemplate(response.data);
      setGroups(response.data.groups || []);
      initialTemplateRef.current = getTemplateSnapshot(response.data);
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  }, [id, service]);

  useEffect(() => {
    calendarService.readAll().then((response) => {
      setCalendarList(response.data);
    }).catch((error: any) => ErrorComponent(error));
  }, [calendarService]);

  useEffect(() => {
    const selectedCalendarOptions = calendarList.filter((calendar) => (
      calendar.id !== undefined && template.calendars.includes(calendar.id)
    ));
    setCalendarOptTasks(selectedCalendarOptions);
  }, [calendarList, template.calendars]);

  const tasks = groups.flatMap((group) => group.tasks);
  const amountOfDays = tasks.reduce((total, task) => total + Number(task.duration || 0), 0);
  const hasTemplateChanges = getTemplateSnapshot(template) !== initialTemplateRef.current;
  const selectedDeleteGroup = deleteGroupId === null ? null : groups.find((group) => group.id === deleteGroupId) || null;
  const selectedDeleteTask = deleteTask === null ? null : groups
    .find((group) => group.id === deleteTask.groupId)
    ?.tasks.find((task) => task.id === deleteTask.taskId) || null;

  const getChildren = (parentId: number | null): TemplateGroup[] => {
    return groups
      .filter((group) => (group.ancestor_group ?? null) === parentId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  const rootGroups = getChildren(null);

  const getFirstTaskId = (parentId: number | null): number | undefined => {
    for (const group of getChildren(parentId)) {
      const firstTaskId = group.tasks[0]?.id ?? getFirstTaskId(group.id);
      if (firstTaskId !== undefined) {
        return firstTaskId;
      }
    }
    return undefined;
  };

  const firstTaskId = getFirstTaskId(null);

  const hasGroupWithSameName = (name: string, parentId: number | null, ignoredGroupId?: number) => {
    return getChildren(parentId).some((group) => (
      group.id !== ignoredGroupId && group.name.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase()
    ));
  };

  const hasTaskWithSameAcronym = (acronym: string, ignoredTaskId?: number) => {
    return groups.some((group) => group.tasks.some((task) => (
      task.id !== ignoredTaskId && task.acronym.trim().toLocaleLowerCase() === acronym.trim().toLocaleLowerCase()
    )));
  };

  const addingGroupNameAlreadyExists = addingGroupParentId !== undefined && addingGroupName.trim() !== "" && hasGroupWithSameName(addingGroupName, addingGroupParentId);
  const editingGroup = editingGroupId === null ? null : groups.find((group) => group.id === editingGroupId) || null;
  const editingGroupNameAlreadyExists = (
    editingGroup !== null &&
    editingGroupName.trim() !== "" &&
    hasGroupWithSameName(editingGroupName, editingGroup.ancestor_group ?? null, editingGroup.id)
  );
  const draftTaskAcronymAlreadyExists = draftTask.acronym.trim() !== "" && hasTaskWithSameAcronym(draftTask.acronym);

  useEffect(() => {
    if (!shouldScrollToNewGroupRef.current) {
      return;
    }

    shouldScrollToNewGroupRef.current = false;
    groupsEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [groups.length]);

  const handleTemplateChange = <K extends keyof Template>(field: K, value: Template[K]) => {
    setTemplate((currentTemplate) => ({
      ...currentTemplate,
      [field]: value,
    }));
  };

  const handleCalendarChange = (calendar: number) => {
    setTemplate((currentTemplate) => {
      const selectedCalendars = currentTemplate.calendars.includes(calendar)
        ? currentTemplate.calendars.filter((item) => item !== calendar)
        : [...currentTemplate.calendars, calendar];

      return {
        ...currentTemplate,
        calendars: selectedCalendars,
      };
    });
  };

  const handleSave = () => {
    if (!id || !template.name.trim() || !template.project_type || !template.calendars.length || !hasTemplateChanges) {
      return;
    }

    setLoadingRequest(true);

    service.patch(Number(id), {
      name: template.name.trim(),
      project_type: Number(template.project_type),
      calendars: template.calendars,
      amount_of_days: amountOfDays,
      active: template.active,
    }).then((response) => {
      setTemplate(response.data);
      initialTemplateRef.current = getTemplateSnapshot(response.data);
      toast.success(translate.t("successfully_updated"));
    }).catch((error: any) => {
      ErrorComponent(error);
    }).finally(() => {
      setLoadingRequest(false);
    });
  };

  const handleStartAddGroup = (parentId: number | null) => {
    if (parentId !== null) {
      expandGroup(parentId);
    }

    setAddingGroupName("");
    setAddingGroupParentId(parentId);
  };

  const handleCancelAddGroup = () => {
    setAddingGroupParentId(undefined);
    setAddingGroupName("");
  };

  const handleAddGroupKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (!addingGroupName.trim() || addingGroupNameAlreadyExists || loadingRequest) return;
    handleConfirmAddGroup();
  };

  const handleConfirmAddGroup = () => {
    const groupName = addingGroupName.trim();

    if (!groupName || !id || addingGroupParentId === undefined) {
      return;
    }

    if (hasGroupWithSameName(groupName, addingGroupParentId)) {
      toast.error(translate.t("group_already_added"));
      return;
    }

    setLoadingRequest(true);

    groupService.create({
      template: Number(id),
      name: groupName,
      ancestor_group: addingGroupParentId,
    } as unknown as TemplateGroup).then((response) => {
      shouldScrollToNewGroupRef.current = true;
      setGroups((currentGroups) => [
        ...currentGroups,
        { ...response.data, tasks: response.data.tasks ?? [] },
      ]);
      toast.success(translate.t("successfully_registered"));
      handleCancelAddGroup();
    }).catch((error: any) => {
      ErrorComponent(error);
    }).finally(() => {
      setLoadingRequest(false);
    });
  };

  const collectDescendantIds = (parentId: number): number[] => {
    const children = groups.filter((group) => (group.ancestor_group ?? null) === parentId);
    return children.reduce<number[]>((acc, child) => (
      [...acc, child.id, ...collectDescendantIds(child.id)]
    ), []);
  };

  const handleConfirmDeleteGroup = () => {
    if (deleteGroupId === null) {
      return;
    }

    groupService.delete(deleteGroupId).then(() => {
      setGroups((currentGroups) => {
        const removedGroupIds = new Set([deleteGroupId, ...collectDescendantIds(deleteGroupId)]);
        const deletedTaskIds = currentGroups
          .filter((group) => removedGroupIds.has(group.id))
          .flatMap((group) => group.tasks.map((task) => task.id));

        return currentGroups
          .filter((group) => !removedGroupIds.has(group.id))
          .map((group) => ({
            ...group,
            tasks: group.tasks.map((task) => ({
              ...task,
              predecessor: getPredecessorIds(task.predecessor).filter((item) => !deletedTaskIds.includes(item)),
            })),
          }));
      });
      toast.success(translate.t("successfully_deleted"));
      setDeleteGroupId(null);
      setAddingTaskGroupId(null);
      setAddingGroupParentId(undefined);
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  };

  const handleStartEditGroup = (group: TemplateGroup) => {
    setEditingGroupId(group.id);
    setEditingGroupName(group.name);
  };

  const handleCancelEditGroup = () => {
    setEditingGroupId(null);
    setEditingGroupName("");
  };

  const handleEditGroupKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (!editingGroupName.trim() || editingGroupNameAlreadyExists || loadingRequest) return;
    handleConfirmEditGroup();
  };

  const handleConfirmEditGroup = () => {
    const groupName = editingGroupName.trim();

    if (!groupName || editingGroupId === null || !editingGroup) {
      return;
    }

    if (hasGroupWithSameName(groupName, editingGroup.ancestor_group ?? null, editingGroupId)) {
      toast.error(translate.t("group_already_added"));
      return;
    }

    setLoadingRequest(true);

    groupService.patch(editingGroupId, { name: groupName }).then(() => {
      setGroups((currentGroups) => currentGroups.map((group) => (
        group.id === editingGroupId ? { ...group, name: groupName } : group
      )));
      toast.success(translate.t("successfully_updated"));
      handleCancelEditGroup();
    }).catch((error: any) => {
      ErrorComponent(error);
    }).finally(() => {
      setLoadingRequest(false);
    });
  };


  const handleStartAddTask = (groupId: number) => {
    expandGroup(groupId);
    setDraftTask(emptyDraftTask);
    setAddingTaskGroupId(groupId);
  };

  const handleCancelAddTask = () => {
    setAddingTaskGroupId(null);
    setDraftTask(emptyDraftTask);
  };

  const isDraftTaskValid = (
    draftTask.name.trim() !== "" &&
    draftTask.acronym.trim() !== "" &&
    draftTask.status.trim() !== "" &&
    !!draftTask.resource &&
    draftTask.duration > 0
  );

  const handleConfirmAddTask = () => {
    if (!addingTaskGroupId || !isDraftTaskValid) {
      return;
    }

    if (hasTaskWithSameAcronym(draftTask.acronym)) {
      toast.error(translate.t("task_acronym_already_added"));
      return;
    }

    taskService.create({
      group: addingTaskGroupId,
      name: draftTask.name.trim(),
      acronym: draftTask.acronym.trim(),
      status: draftTask.status.trim(),
      resource: draftTask.resource,
      duration: draftTask.duration,
    } as unknown as TemplateTask).then((response) => {
      setGroups((currentGroups) => currentGroups.map((group) => (
        group.id === addingTaskGroupId
          ? { ...group, tasks: [...group.tasks, response.data] }
          : group
      )));
      toast.success(translate.t("successfully_registered"));
      handleCancelAddTask();
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  };

  const handleConfirmDeleteTask = () => {
    if (deleteTask === null) {
      return;
    }

    taskService.delete(deleteTask.taskId).then(() => {
      setGroups((currentGroups) => currentGroups.map((group) => {
        const remainingTasks = group.id === deleteTask.groupId
          ? group.tasks.filter((task) => task.id !== deleteTask.taskId)
          : group.tasks;

        return {
          ...group,
          tasks: remainingTasks.map((task) => ({
            ...task,
            predecessor: getPredecessorIds(task.predecessor).filter((item) => item !== deleteTask.taskId),
          })),
        };
      }));
      toast.success(translate.t("successfully_deleted"));
      setDeleteTask(null);
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  };

  const handleTaskChange = <K extends keyof TemplateTask>(
    groupId: number,
    activityId: number,
    field: K,
    value: TemplateTask[K]
  ) => {
    setGroups((currentGroups) => currentGroups.map((group) => {
      if (group.id !== groupId) {
        return group;
      }

      return {
        ...group,
        tasks: group.tasks.map((task) => (
          task.id === activityId ? { ...task, [field]: value } : task
        )),
      };
    }));
  };

  const handleTaskFieldCommit = (groupId: number, taskId: number, field: keyof TemplateTask) => {
    markTaskTouched(taskId);

    const group = groups.find((item) => item.id === groupId);
    const task = group?.tasks.find((item) => item.id === taskId);

    if (!task) {
      return;
    }

    if (field === "acronym" && hasTaskWithSameAcronym(String(task[field]), taskId)) {
      toast.error(translate.t("task_acronym_already_added"));
      return;
    }

    taskService.patch(taskId, { [field]: task[field] } as any).catch((error: any) => {
      ErrorComponent(error);
    });
  };

  const handlePredecessorChange = (groupId: number, taskId: number, value: number[]) => {
    const previousValue = groups
      .find((group) => group.id === groupId)
      ?.tasks.find((task) => task.id === taskId)
      ?.predecessor ?? [];

    handleTaskChange(groupId, taskId, "predecessor", value);

    taskService.patch(taskId, { predecessor: value } as any).catch((error: any) => {
      handleTaskChange(groupId, taskId, "predecessor", previousValue);
      ErrorComponent(error);
    });
  };

  const handleResourceChange = (groupId: number, taskId: number, value: TemplateTask["resource"]) => {
    markTaskTouched(taskId);
    handleTaskChange(groupId, taskId, "resource", value);

    if (!value) {
      return;
    }

    taskService.patch(taskId, { resource: value } as any).catch((error: any) => {
      ErrorComponent(error);
    });
  };


  const handleDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;

    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
      return;
    }

    if (type === "GROUP") {
      const parentId = source.droppableId === ROOT_DROPPABLE_ID
        ? null
        : Number(source.droppableId.replace("subgroups-", ""));

      const siblings = getChildren(parentId);
      const draggedGroup = siblings[source.index];
      const anchorGroup = getReorderAnchor(siblings, source.index, destination.index);

      if (!draggedGroup || !anchorGroup) {
        return;
      }

      const previousGroups = groups;

      setGroups((current) => {
        const reordered = [...siblings];
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);
        const orderById = new Map(reordered.map((group, index) => [group.id, index]));

        return current.map((group) => (
          orderById.has(group.id) ? { ...group, order: orderById.get(group.id) } : group
        ));
      });

      groupService
        .reorder(anchorGroup.id, draggedGroup.id)
        .catch((error: any) => {
          setGroups(previousGroups);
          ErrorComponent(error);
        });

      return;
    }

    if (type === "TASK") {
      const sourceGroupId = Number(source.droppableId.replace("tasks-", ""));
      const destinationGroupId = Number(destination.droppableId.replace("tasks-", ""));

      if (sourceGroupId === destinationGroupId) {
        const group = groups.find((item) => item.id === sourceGroupId);
        if (!group) {
          return;
        }

        const draggedTask = group.tasks[source.index];
        const anchorTask = getReorderAnchor(group.tasks, source.index, destination.index);

        if (!draggedTask || !anchorTask) {
          return;
        }

        const previousGroups = groups;

        setGroups((current) =>
          current.map((item) => {
            if (item.id !== sourceGroupId) {
              return item;
            }
            const nextTasks = [...item.tasks];
            const [moved] = nextTasks.splice(source.index, 1);
            nextTasks.splice(destination.index, 0, moved);

            return { ...item, tasks: nextTasks };
          }));

        taskService
          .reorder(anchorTask.id, draggedTask.id)
          .catch((error: any) => {
            setGroups(previousGroups);
            ErrorComponent(error);
          });

        return;
      }

      const sourceGroup = groups.find((item) => item.id === sourceGroupId);
      const movedTask = sourceGroup?.tasks[source.index];

      if (!movedTask) {
        return;
      }

      const previousGroups = groups;

      setGroups((current) => current.map((item) => {
        if (item.id === sourceGroupId) {
          return { ...item, tasks: item.tasks.filter((task) => task.id !== movedTask.id) };
        }
        if (item.id === destinationGroupId) {
          return { ...item, tasks: [...item.tasks, movedTask] };
        }
        return item;
      }));

      taskService
        .moveToGroup(movedTask.id, destinationGroupId)
        .then((response: any) => {
          setGroups((current) => current.map((item) => (
            item.id === destinationGroupId
              ? { ...item, tasks: item.tasks.map((task) => (task.id === movedTask.id ? response.data : task)) }
              : item
          )));
        })
        .catch((error: any) => {
          setGroups(previousGroups);
          ErrorComponent(error);
        });
    }
  };

  const renderTasksTable = (group: TemplateGroup) => (
   <Box sx={{ px: 1, py: 1, minWidth: TASKS_TABLE_MIN_WIDTH }}>
      <Grid
        container
        alignItems="center"
        columnSpacing={1.5}
        sx={{ display: "grid", gridTemplateColumns: "22px minmax(160px, 2fr) minmax(90px, 1.2fr) minmax(110px, 1.25fr) minmax(110px, 0.7fr) minmax(90px, 0.65fr) minmax(140px, 1.4fr) 32px", px: 1, py: 1 }}>
        <Typography className="npi-template-header-cell" />
        <Typography className="npi-template-header-cell">{translate.t("task_name")}</Typography>
        <Typography className="npi-template-header-cell">{translate.t("task_acronym")}</Typography>
        <Typography className="npi-template-header-cell">{translate.t("status")}</Typography>
        <Typography className="npi-template-header-cell">{translate.t("resource")}</Typography>
        <Typography className="npi-template-header-cell">{translate.t("deadline")}</Typography>
        <Typography className="npi-template-header-cell">{translate.t("predecessor")}</Typography>
        <Typography className="npi-template-header-cell" />
      </Grid>

      {addingTaskGroupId === group.id && (
        <Grid
          container
          alignItems="flex-start"
          columnSpacing={1.5}
          rowSpacing={1}
          sx={{
            border: "1px dashed #8DA0B8",
            borderRadius: "8px",
            backgroundColor: "#EEF2F7",
            display: "grid",
            gridTemplateColumns: "22px minmax(160px, 2fr) minmax(90px, 1.2fr) minmax(110px, 1.25fr) minmax(110px, 0.7fr) minmax(90px, 0.65fr) minmax(140px, 1.2fr) 70px",
            px: 1,
            py: 0.75,
            mb: 0.75,
          }}>
          <DragIndicator sx={{ color: "#91A4BC", fontSize: 18, opacity: 0.3, alignSelf: "center" }} />
          <TextField
            value={draftTask.name}
            placeholder={`${translate.t("task_name")}*`}
            size="small"
            autoFocus
            multiline
            maxRows={3}
            className="npi-template-field"
            onChange={(event) => setDraftTask((current) => ({ ...current, name: event.target.value }))}
            inputProps={{ maxLength: 256 }}
          />
          <TextField
            value={draftTask.acronym}
            placeholder={`${translate.t("task_acronym")}*`}
            size="small"
            className="npi-template-field"
            error={draftTaskAcronymAlreadyExists}
            helperText={draftTaskAcronymAlreadyExists ? translate.t("task_acronym_already_added") : ""}
            onChange={(event) => setDraftTask((current) => ({ ...current, acronym: event.target.value }))}
            inputProps={{ maxLength: 6 }}
          />
          <TextField
            value={draftTask.status}
            placeholder={`${translate.t("task_status")}*`}
            size="small"
            className="npi-template-field"
            onChange={(event) => setDraftTask((current) => ({ ...current, status: event.target.value }))}
            inputProps={{ maxLength: 64 }}
          />
          <FormControl size="small" className="npi-template-field">
            <Select
              displayEmpty
              value={draftTask.resource}
              onChange={(event) => setDraftTask((current) => ({ ...current, resource: event.target.value }))}>
              <MenuItem value="">{`${translate.t("select")}*`}</MenuItem>
              {calendarOptTasks.map((calendar) => (
                <MenuItem key={calendar.id} value={calendar.type}>{calendarTypeLabels[calendar.type]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            value={draftTask.duration}
            type="number"
            inputProps={{ min: 1 }}
            size="small"
            className="npi-template-field"
            onChange={(event) => setDraftTask((current) => ({ ...current, duration: Number(event.target.value) }))}
            InputProps={{ endAdornment: <InputAdornment position="end">{translate.t("dias")}</InputAdornment> }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {translate.t("save_to_set_predecessor")}
          </Typography>
          <Box sx={{ display: "flex" }}>
            <IconButton
              size="small"
              disabled={!isDraftTaskValid || draftTaskAcronymAlreadyExists}
              onClick={handleConfirmAddTask}
              aria-label={translate.t("save")}>
              <Check />
            </IconButton>
            <IconButton size="small" onClick={handleCancelAddTask} aria-label={translate.t("close")}>
              <Close />
            </IconButton>
          </Box>
        </Grid>
      )}

      <Droppable droppableId={`tasks-${group.id}`} type="TASK">
        {(tasksDroppableProvided) => (
          <Stack
            spacing={0.75}
            ref={tasksDroppableProvided.innerRef}
            {...tasksDroppableProvided.droppableProps}>
            {group.tasks.map((task, taskIndex) => (
              <Draggable draggableId={`task-${task.id}`} index={taskIndex} key={task.id}>
                {(taskDragProvided) => (
                  <Grid
                    ref={taskDragProvided.innerRef}
                    {...taskDragProvided.draggableProps}
                    container
                    alignItems="center"
                    columnSpacing={1.5}
                    rowSpacing={1}
                    sx={{
                      border: "1px solid #C9D9EE",
                      borderRadius: "8px",
                      backgroundColor: "#F8FBFF",
                      display: "grid",
                      gridTemplateColumns: "22px minmax(160px, 2fr) minmax(90px, 1.2fr) minmax(110px, 1.25fr) minmax(110px, 0.7fr) minmax(90px, 0.65fr) minmax(140px, 1.4fr) 32px",
                      px: 1,
                      py: 0.75,
                      "& .MuiInputBase-input": { fontWeight: 400 },
                    }}>
                    <span {...taskDragProvided.dragHandleProps} style={{ display: "flex" }}>
                      <DragIndicator sx={{ color: "#91A4BC", fontSize: 18 }} />
                    </span>
                    <TextField
                      sx={{
                        width: "100%",
                        minWidth: "80px",
                        "& .MuiInputBase-root": {
                          height: editingNameTaskId === task.id ? "auto" : "100%",
                          alignItems: "center",
                          overflow: "hidden",
                        },
                        "& .MuiInputBase-input": {
                          padding: "0",
                        },
                      }}
                      value={task.name}
                      placeholder={translate.t("task_name")}
                      size="small"
                      required
                      multiline
                      maxRows={3}
                      error={taskIds.has(task.id) && !task.name.trim()}
                      onChange={(event) => handleTaskChange(group.id, task.id, "name", event.target.value)}
                      onFocus={() => setEditingNameTaskId(task.id)}
                      onBlur={() => {
                        handleTaskFieldCommit(group.id, task.id, "name");
                        setEditingNameTaskId(null);
                      }}
                      className="npi-template-field"
                      inputProps={{ maxLength: 256 }}
                    />
                    <TextField
                      sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                          height: "100%",
                        },
                      }}
                      value={task.acronym}
                      placeholder={translate.t("task_acronym")}
                      size="small"
                      required
                      error={
                        (taskIds.has(task.id) && !task.acronym.trim()) ||
                        (task.acronym.trim() !== "" && hasTaskWithSameAcronym(task.acronym, task.id))
                      }
                      helperText={
                        task.acronym.trim() !== "" && hasTaskWithSameAcronym(task.acronym, task.id)
                          ? translate.t("task_acronym_already_added")
                          : ""
                      }
                      className="npi-template-field"
                      onChange={(event) => handleTaskChange(group.id, task.id, "acronym", event.target.value)}
                      onBlur={() => handleTaskFieldCommit(group.id, task.id, "acronym")}
                      inputProps={{ maxLength: 6 }}
                    />
                    <TextField
                      sx={{
                        width: "100%",
                        "& .MuiInputBase-root": {
                          height: "100%",
                        },
                      }}
                      value={task.status}
                      placeholder={translate.t("task_status")}
                      size="small"
                      required
                      error={taskIds.has(task.id) && !task.status.trim()}
                      className="npi-template-field"
                      onChange={(event) => handleTaskChange(group.id, task.id, "status", event.target.value)}
                      onBlur={() => handleTaskFieldCommit(group.id, task.id, "status")}
                      inputProps={{ maxLength: 64 }}
                    />
                    <FormControl
                      size="small"
                      required
                      error={taskIds.has(task.id) && !task.resource}
                      className="npi-template-field"
                      sx={{
                        height: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: "100%",
                        },
                      }}>
                      <Select
                        sx={{width:"100%", minWidth: "80px"}}
                        displayEmpty
                        value={task.resource}
                        onChange={(event) => handleResourceChange(group.id, task.id, event.target.value)}>
                        <MenuItem value="">{translate.t("select")}</MenuItem>
                        {calendarOptTasks.map((calendar) => (
                          <MenuItem key={calendar.id} value={calendar.type}>{calendarTypeLabels[calendar.type]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "100%",
                        },
                      }}
                      value={task.duration}
                      type="number"
                      size="small"
                      inputProps={{ min: 1 }}
                      required
                      error={taskIds.has(task.id) && !task.duration}
                      className="npi-template-field"
                      onChange={(event) => handleTaskChange(group.id, task.id, "duration", Number(event.target.value))}
                      onBlur={() => handleTaskFieldCommit(group.id, task.id, "duration")}
                      InputProps={{ endAdornment: <InputAdornment position="end">{translate.t("dias")}</InputAdornment> }}
                    />
                    <Box
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        height: "100%",
                        display: "flex",
                        alignItems: "stretch",
                        "& .MuiFormControl-root": {
                          height: "100%",
                        },
                        "& .MuiInputBase-root": {
                          height: "100%",
                        },
                      }}>
                      <PredecessorSelectComponent
                        currentTaskId={task.id}
                        groups={groups}
                        value={task.predecessor}
                        onChange={(value) => handlePredecessorChange(group.id, task.id, value)}
                        disabled={task.id === firstTaskId && getPredecessorIds(task.predecessor).length === 0}
                      />
                    </Box>
                    <IconButton size="small" onClick={() => setDeleteTask({ groupId: group.id, taskId: task.id })} aria-label={translate.t("delete")}>
                      <DeleteOutline />
                    </IconButton>
                  </Grid>
                )}
              </Draggable>
            ))}
            {tasksDroppableProvided.placeholder}
            {group.tasks.length === 0 && addingTaskGroupId !== group.id && (
              <Typography textAlign="center" padding="10px">Nenhuma atividade adicionada</Typography>
            )}
          </Stack>
        )}
      </Droppable>
    </Box>
  );

  const renderGroupNode = (group: TemplateGroup, index: number, depth: number) => {
    const children = getChildren(group.id);
    const isCollapsed = collapsedGroupIds.has(group.id);
    const canAddSubgroup = depth < MAX_SUBGROUP_DEPTH;

    return (
      <Draggable draggableId={`group-${group.id}`} index={index} key={group.id}>
        {(groupDragProvided) => (
          <Paper
            ref={groupDragProvided.innerRef}
            {...groupDragProvided.draggableProps}
            variant="outlined"
            sx={{ borderColor: "#DCE5F2", borderRadius: "16px", boxShadow: "none", mb: 2, ml: depth * 3, overflow: "hidden", backgroundColor: "#FFFFFF", minWidth: 0 }}
          >
            <Grid container alignItems="center" sx={{ backgroundColor: "#EEF2F7", px: 1.5, py: 1.5 }}>
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1 }}>
                <span {...groupDragProvided.dragHandleProps} style={{ display: "flex" }}>
                  <DragIndicator sx={{ color: "#8DA0B8", fontSize: 20, alignSelf: "center" }} />
                </span>
                <IconButton
                  size="small"
                  onClick={() => handleToggleGroupCollapse(group.id)}
                  aria-label={translate.t(isCollapsed ? "expand_group" : "collapse_group")}
                  aria-expanded={!isCollapsed}>
                  {isCollapsed ? <ChevronRight /> : <ExpandMore />}
                </IconButton>
                {editingGroupId === group.id ? (
                  <TextField
                    value={editingGroupName}
                    size="small"
                    fullWidth
                    autoFocus
                    error={editingGroupNameAlreadyExists}
                    helperText={editingGroupNameAlreadyExists ? translate.t("group_already_added") : ""}
                    inputProps={{ maxLength: 256 }}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value.length > 256) return;
                      setEditingGroupName(value);
                    }}
                    onKeyDown={handleEditGroupKeyDown}
                  />
                ) : (
                  <Typography sx={{ fontWeight: 900, fontSize: 20 }}>{group.name}</Typography>
                )}
              </Grid>
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {editingGroupId === group.id ? (
                  <>
                    <IconButton
                      size="small"
                      disabled={!editingGroupName.trim() || editingGroupNameAlreadyExists || loadingRequest}
                      onClick={handleConfirmEditGroup}
                      aria-label={translate.t("save")}
                    >
                      <Check />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEditGroup} aria-label={translate.t("close")}>
                      <Close />
                    </IconButton>
                  </>
                ) : (
                  <>
                  <Tooltip title={translate.t("new_activity")}>
                    <span>
                      <IconButton size="small" disabled={addingTaskGroupId !== null} onClick={() => handleStartAddTask(group.id)} aria-label={translate.t("new_activity")}>
                        <Add />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {canAddSubgroup && (
                    <Tooltip title={translate.t("new_subgroup")}>
                      <span>
                        <IconButton size="small" disabled={addingGroupParentId !== undefined} onClick={() => handleStartAddGroup(group.id)} aria-label={translate.t("new_subgroup")}>
                          <LibraryAdd />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={translate.t("edit")}>
                    <span>
                      <IconButton size="small" onClick={() => handleStartEditGroup(group)} aria-label={translate.t("edit")}>
                        <Edit />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={translate.t("delete")}>
                    <span>
                      <IconButton size="small" onClick={() => setDeleteGroupId(group.id)} aria-label={translate.t("delete")}>
                        <DeleteOutline />
                      </IconButton>
                    </span>
                  </Tooltip>
                  </>
                )}
              </Grid>
            </Grid>
            {!isCollapsed && (
              <>
                {addingGroupParentId === group.id && (
                  <Box sx={{ px: 1.5, py: 1.5 }}>
                    <Box flexDirection="row" justifyContent="space-between" alignItems="center" display="flex">
                      <Box sx={{ display: "initial", textAlign: "left" }} width="50%">
                        <TextField
                          value={addingGroupName}
                          placeholder={`${translate.t("new_subgroup")}*`}
                          className="npi-template-field"
                          fullWidth
                          autoFocus
                          error={addingGroupNameAlreadyExists}
                          helperText={addingGroupNameAlreadyExists ? translate.t("group_already_added") : ""}
                          inputProps={{ maxLength: 256 }}
                          onChange={(event) => {
                            const value = event.target.value;
                            if (value.length > 256) return;
                            setAddingGroupName(value);
                          }}
                          onKeyDown={handleAddGroupKeyDown}
                        />
                      </Box>
                      <Box sx={{ display: "initial", textAlign: "right" }}>
                        <IconButton
                          disabled={!addingGroupName.trim() || addingGroupNameAlreadyExists || loadingRequest}
                          onClick={handleConfirmAddGroup}
                          aria-label={translate.t("save")}
                        >
                          <Check />
                        </IconButton>
                        <IconButton onClick={handleCancelAddGroup} aria-label={translate.t("close")}>
                          <Close />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                )}

                {renderTasksTable(group)}

                {children.length > 0 && (
                  <Droppable droppableId={`subgroups-${group.id}`} type="GROUP">
                    {(subgroupsDroppableProvided) => (
                      <Box
                        sx={{ px: 1.5, pb: 1.5, minWidth: 0 }}
                        ref={subgroupsDroppableProvided.innerRef}
                        {...subgroupsDroppableProvided.droppableProps}
                      >
                        {children.map((child, childIndex) => renderGroupNode(child, childIndex, depth + 1))}
                        {subgroupsDroppableProvided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                )}
              </>
            )}
          </Paper>
        )}
      </Draggable>
    );
  };

  return (
    <section className="npi-template-detail-page" style={{ minWidth: 0 }}>
      <header className="npi-template-detail-header">
        <div className="npi-template-detail-title">
          <Typography variant="h5">
            {translate.t("template_detail")}
          </Typography>
        </div>
        <Stack direction="row" spacing={1}>
          <Button color="secondary" variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(URLS.NPI_TEMPLATE)}>
            {translate.t("back").toUpperCase()}
          </Button>
          <Button color="secondary" variant="contained" startIcon={<Check />} onClick={handleSave}
            disabled={!template.name.trim() || !template.project_type || !template.calendars.length || !hasTemplateChanges || loadingRequest}>
            {translate.t("save").toUpperCase()}
          </Button>
        </Stack>
      </header>

      <Box className="npi-template-detail-form">
        <TextField
          label={`${translate.t("template_name")}*`}
          value={template.name}
          onChange={(event) => {
            const value = event.target.value;
            if (value.length > 256) return;
            handleTemplateChange("name", value);
          }}
          sx={{ width: '50%' }}
          inputProps={{ maxLength: 256 }}
        />
        <CrudSelectProjectTypeComponent
          value={template.project_type}
          onChange={(value) => handleTemplateChange("project_type", value)}
          selectSx={{ width: '50%' }}
          formControlSx={{
            "& .MuiOutlinedInput-root": {
              height: "52px",
            },
            "& .MuiSelect-select": {
              alignItems: "center",
              display: "flex",
            },
          }}
        />
        <Box className="npi-template-detail-calendar">
          <Typography marginLeft="10px" fontWeight="600">{translate.t("calendar")}:</Typography>
          <FormGroup row>
            {calendarList.map((calendar) => (
              <FormControlLabel
                key={calendar.id}
                control={
                  <Checkbox
                    checked={template.calendars.includes(calendar.id!!)}
                    onChange={() => handleCalendarChange(calendar.id!!)}
                  />
                }
                sx={{ "& .MuiFormControlLabel-label": { fontWeight: 400 } }}
                label={capitalizeFirstLetter(calendarTypeLabels[calendar.type])}
              />
            ))}
          </FormGroup>
        </Box>
      </Box>

      <Box className="npi-template-detail-summary">
        <Stack direction="row" spacing={5} className="npi-template-detail-summary-content" gap="30px">
          <Box className="npi-template-detail-metric">
            <Typography>{translate.t("total_deadline")}</Typography>
            <Typography>
              {`${amountOfDays} ${amountOfDays > 1 ? translate.t("dias") : amountOfDays == 1 ? translate.t("dia") : ''}`}
            </Typography>
          </Box>
          <Box className="npi-template-detail-metric">
            <Typography>{translate.t("phases")}</Typography>
            <Typography>{rootGroups.length}</Typography>
          </Box>
          <Box className="npi-template-detail-metric">
            <Typography>{translate.t("activities")}</Typography>
            <Typography>{tasks.length}</Typography>
          </Box>
          <Box className="npi-template-detail-new-group">
            <Button
              disabled={addingGroupParentId !== undefined}
              color="secondary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleStartAddGroup(null)}
            >
              {translate.t("new_group").toUpperCase()}
            </Button>
          </Box>
        </Stack>
      </Box>

      {addingGroupParentId === null && (
        <Box className="npi-template-detail-groups npi-template-detail-groups-entry">
          <Paper
            variant="outlined"
            sx={{ borderColor: "#DCE5F2", borderRadius: "16px", overflow: "hidden", backgroundColor: "#EEF2F7", padding: "10px" }}
          >
            <Box flexDirection="row" justifyContent="space-between" alignItems="center" display="flex">
              <Box sx={{ display: "initial", textAlign: "left" }} width="50%">
                <TextField
                  value={addingGroupName}
                  placeholder={`${translate.t("name_of_group")}*`}
                  className="npi-template-field"
                  fullWidth
                  autoFocus
                  error={addingGroupNameAlreadyExists}
                  helperText={addingGroupNameAlreadyExists ? translate.t("group_already_added") : ""}
                  inputProps={{ maxLength: 256 }}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value.length > 256) return;
                    setAddingGroupName(value);
                  }}
                  onKeyDown={handleAddGroupKeyDown}
                />
              </Box>
              <Box sx={{ display: "initial", textAlign: "right" }}>
                <IconButton
                  disabled={!addingGroupName.trim() || addingGroupNameAlreadyExists || loadingRequest}
                  onClick={handleConfirmAddGroup}
                  aria-label={translate.t("save")}
                >
                  <Check />
                </IconButton>
                <IconButton onClick={handleCancelAddGroup} aria-label={translate.t("close")}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        {rootGroups.length > 0 && (
          <Droppable droppableId={ROOT_DROPPABLE_ID} type="GROUP">
            {(groupsDroppableProvided) => (
              <Box
                className="npi-template-detail-groups npi-template-detail-groups-list"
                sx={{ minWidth: 0, overflowX: "auto" }}
                ref={groupsDroppableProvided.innerRef}
                {...groupsDroppableProvided.droppableProps}>
                <Box sx={{ width: "min-content", minWidth: "100%", pb: `${MENU_MIN_HEIGHT - 150}px` }}>
                  {rootGroups.map((group, groupIndex) => renderGroupNode(group, groupIndex, 0))}
                  {groupsDroppableProvided.placeholder}
                  <div ref={groupsEndRef} />
                </Box>
              </Box>
            )}
          </Droppable>
        )}
      </DragDropContext>

      <Dialog open={deleteGroupId !== null} onClose={() => setDeleteGroupId(null)}>
        <Grid p={2} container justifyContent="space-between" sx={{ backgroundColor: "primary.main" }}>
          <Grid>
            <Typography sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}>
              {translate.t("delete")}
            </Typography>
          </Grid>
          <Grid sx={{ cursor: "pointer", color: "#ffffff" }}>
            <Close onClick={() => setDeleteGroupId(null)} />
          </Grid>
        </Grid>
        <DialogContent>
          <Grid container flexDirection="column" gap={2}>
            <Typography>{translate.t("confirm_delete_group")}</Typography>
            <Typography sx={{ fontWeight: "bold" }}>{selectedDeleteGroup?.name}</Typography>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="flex-end" gap={1} p={2}>
            <Button color="secondary" variant="outlined" onClick={() => setDeleteGroupId(null)}>
              {translate.t("cancel")}
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDeleteGroup}>
              {translate.t("delete")}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteTask !== null} onClose={() => setDeleteTask(null)}>
        <Grid p={2} container justifyContent="space-between" sx={{ backgroundColor: "primary.main" }}>
          <Grid>
            <Typography sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}>
              {translate.t("delete")}
            </Typography>
          </Grid>
          <Grid sx={{ cursor: "pointer", color: "#ffffff" }}>
            <Close onClick={() => setDeleteTask(null)} />
          </Grid>
        </Grid>
        <DialogContent>
          <Grid container flexDirection="column" gap={2}>
            <Typography>{translate.t("confirm_delete_activity")}</Typography>
            <Typography sx={{ fontWeight: "bold" }}>{selectedDeleteTask?.name}</Typography>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="flex-end" gap={1} p={2}>
            <Button color="secondary" variant="outlined" onClick={() => setDeleteTask(null)}>
              {translate.t("cancel")}
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDeleteTask}>
              {translate.t("delete")}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>

      {addingGroupParentId === undefined && rootGroups.length === 0 && (
        <Box className="npi-template-detail-groups npi-template-detail-groups-list">
          <Paper
            variant="outlined"
            sx={{ borderColor: "#DCE5F2", borderRadius: "16px", overflow: "hidden", backgroundColor: "#EEF2F7", textAlign: "center", padding: "10px" }}
          >
            Nenhum grupo adicionado ao template
          </Paper>
        </Box>
      )}
    </section>
  );
};

export default NpiTemplateDetailComponent;
